'use client';

import React, { useEffect, useMemo, useState } from 'react';

import { Accordion, Tab, Tabs } from 'lighthouse';

import InnerHtml from '@/widgets/InnerHtml';

import { GregorianToJalali } from '@/utilities/gregorianTojalali';

import { ReviewsSection } from '../ReviewsSection';
import { ProductDetailsTabsProps, ProductDetailsTabValue } from './models';

import { Category, Setting, Star1 } from 'iconsax-react';

export const ProductDetailsTabs = ({
    description,
    attributes,
    reviewsUuid,
    onAddReview,
    canAddReview,
    onSummaryChange,
    ratingSummary,
    activeTab,
    setActiveTab
}: ProductDetailsTabsProps) => {
    const tabs = useMemo(
        () =>
            [
                {
                    value: 'intro' as const,
                    label: 'معرفی محصول',
                    icon: <Category size={18} color='currentcolor' />,
                    visible: Boolean(description)
                },
                {
                    value: 'specs' as const,
                    label: 'مشخصات فنی',
                    icon: <Setting size={18} color='currentcolor' />,
                    visible: attributes.length > 0
                },
                {
                    value: 'reviews' as const,
                    label: ratingSummary?.count ? `دیدگاه‌ها (${ratingSummary.count})` : 'دیدگاه کاربران',
                    icon: <Star1 size={18} color='currentcolor' />,
                    visible: true
                }
            ].filter((tab) => tab.visible),
        [description, ratingSummary?.count, attributes]
    );

    const [reviewsDefaultExpanded, setReviewsDefaultExpanded] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined' && window.location.hash === '#reviews') {
            setReviewsDefaultExpanded(true);
        }
    }, []);

    if (!tabs.length) return null;

    const handleTabChange = (_: React.SyntheticEvent, value: ProductDetailsTabValue) => {
        setActiveTab(value);
    };

    const renderIntro = () =>
        !!description && (
            <div role='tabpanel' aria-label='معرفی محصول'>
                <InnerHtml content={description} />
            </div>
        );

    const renderSpecs = () =>
        attributes.length > 0 && (
            <div role='tabpanel' aria-label='مشخصات فنی'>
                <div className='space-y-4'>
                    {attributes.map((attribute) => {
                        return (
                            <div key={attribute.groupId} className='overflow-hidden rounded-xl border border-solid border-gray-100'>
                                <div className='flex w-full justify-between bg-gray-200/40 px-4'>
                                    <p className='text-sm font-bold'>{attribute.groupName}</p>
                                </div>
                                {attribute.attributes.map((item) => {
                                    return (
                                        <div
                                            key={item.attributeId}
                                            className='grid grid-cols-2 items-center px-4 odd:border-b odd:bg-gray-100/50 even:bg-white'>
                                            <div className='flex items-center gap-x-2'>
                                                <div className='h-2 w-2 rounded-full bg-gray-300' />
                                                <p className='text-sm font-medium text-gray-600'>{item.name}</p>
                                            </div>
                                            <div className='flex items-center gap-x-1 text-sm font-normal'>
                                                <p>
                                                    {item.type === 'BOOLEAN'
                                                        ? item.value === true
                                                            ? 'بله'
                                                            : 'خیر'
                                                        : item.type === 'DATE_TIME' || item.type === 'DATE'
                                                          ? GregorianToJalali(item.value, 'yyyy/MM/dd')
                                                          : item.value}
                                                </p>
                                                <span>{item.unitName}</span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        );

    const renderReviews = () => (
        <div role='tabpanel' aria-label='دیدگاه کاربران'>
            <ReviewsSection uuid={reviewsUuid} onAddReview={onAddReview} canAddReview={canAddReview} onSummaryChange={onSummaryChange} hideHeader />
        </div>
    );

    const panelByValue: Record<ProductDetailsTabValue, React.ReactNode> = {
        intro: renderIntro() ?? null,
        specs: renderSpecs() ?? null,
        reviews: renderReviews()
    };

    return (
        <>
            <section className='hidden scroll-mt-20 overflow-hidden rounded-lg border border-solid border-gray-100 bg-white shadow-sm md:block'>
                <div className='border-0 border-b border-solid border-b-gray-100 px-2 md:px-4'>
                    <Tabs value={activeTab} onChange={handleTabChange} aria-label='product-details-tabs' variant='fullWidth'>
                        {tabs.map((tab) => (
                            <Tab
                                key={tab.value}
                                value={tab.value}
                                label={
                                    <span className='flex items-center gap-1.5'>
                                        {tab.icon}
                                        {tab.label}
                                    </span>
                                }
                            />
                        ))}
                    </Tabs>
                </div>

                <div className='p-4 md:p-6'>{activeTab && panelByValue[activeTab]}</div>
            </section>

            <section className='flex scroll-mt-20 flex-col gap-2 md:hidden'>
                {tabs.map((tab) => (
                    <div key={tab.value} id={tab.value === 'reviews' ? 'reviews' : undefined} className='scroll-mt-20'>
                        <Accordion
                            typographyProps={{ variant: 'subtitle2', component: 'div' }}
                            accordionShadow='elevation1'
                            accordionSummery={
                                <span className='flex items-center gap-1.5'>
                                    {tab.icon}
                                    {tab.label}
                                </span>
                            }
                            component='div'
                            defaultExpanded={tab.value === 'reviews' ? reviewsDefaultExpanded : false}>
                            <>{panelByValue[tab.value]}</>
                        </Accordion>
                    </div>
                ))}
            </section>
        </>
    );
};
